import { useState } from "react";
import { Mail, Phone, MapPin, ArrowRight, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import SectionHeader from "./SectionHeader";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CONTACTS = [
    {
        icon: Mail,
        label: "Email",
        value: "info@zaroorglobaltrading.com",
        href: "mailto:info@zaroorglobaltrading.com"
    },
    {
        icon: Phone,
        label: "Phone",
        value: "+1 (307) 213-8513",
        href: "tel:+13072138513"
    },
    {
        icon: MapPin,
        label: "Office",
        value: "5830 E 2nd St, STE 7000 #34793, Casper, WY 82609, USA",
        href: "#"
    }
];

export default function Contact() {
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
    const [loading, setLoading] = useState(false);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill name, email and message.");
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form)
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || `Request failed (${res.status})`);
            }
            toast.success("Thanks — our team will reply within 1 business day.");
            setForm({ name: "", company: "", email: "", phone: "", message: "" });
        } catch (err) {
            toast.error(err.message || "Could not send your message. Please email us directly.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" className="py-20 lg:py-28 bg-muted/40 border-t border-border">
            <div className="container-px mx-auto max-w-7xl">
                <SectionHeader
                    eyebrow="Contact"
                    title="Let's talk wholesale."
                    description="Whether you're a brand looking to expand reach, or a buyer sourcing reliable supply — send us a brief and we'll respond within one business day."
                />

                <div className="mt-14 grid lg:grid-cols-12 gap-8 lg:gap-12">
                    {/* Contact cards */}
                    <div className="lg:col-span-5 flex flex-col gap-4">
                        {CONTACTS.map((c) => (
                            <a
                                key={c.label}
                                href={c.href}
                                className="group flex items-start gap-4 p-5 lg:p-6 rounded-xl bg-background border border-border hover:border-primary/30 hover:shadow-soft transition-smooth"
                            >
                                <span className="grid place-items-center w-11 h-11 rounded-lg bg-accent text-accent-foreground border border-border shrink-0">
                                    <c.icon className="w-5 h-5" />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
                                        {c.label}
                                    </p>
                                    <p className="mt-1 text-sm lg:text-base font-medium text-foreground break-words">
                                        {c.value}
                                    </p>
                                </div>
                                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-smooth mt-3" />
                            </a>
                        ))}

                        <div className="mt-2 p-5 lg:p-6 rounded-xl bg-primary text-primary-foreground">
                            <p className="text-[11px] uppercase tracking-[0.18em] text-primary-foreground/70 font-medium">
                                Working hours
                            </p>
                            <p className="mt-2 text-sm lg:text-base font-medium">
                                Monday – Friday · 9:00 AM – 6:00 PM (MT)
                            </p>
                            <p className="mt-1 text-sm text-primary-foreground/70">
                                Replies within 1 business day.
                            </p>
                        </div>
                    </div>

                    {/* Form */}
                    <Card className="lg:col-span-7 border-border shadow-soft bg-background">
                        <CardContent className="p-6 lg:p-8">
                            <form onSubmit={onSubmit} className="flex flex-col gap-5" data-testid="contact-form">
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div className="flex flex-col gap-2">
                                        <Label htmlFor="name" className="text-sm font-medium text-foreground">
                                            Full name
                                        </Label>
                                        <Input
                                            id="name"
                                            value={form.name}
                                            onChange={onChange("name")}
                                            placeholder="Your name"
                                            className="h-11 bg-background"
                                            data-testid="contact-name-input"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <Label htmlFor="company" className="text-sm font-medium text-foreground">
                                            Company
                                        </Label>
                                        <Input
                                            id="company"
                                            value={form.company}
                                            onChange={onChange("company")}
                                            placeholder="Company name"
                                            className="h-11 bg-background"
                                            data-testid="contact-company-input"
                                        />
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div className="flex flex-col gap-2">
                                        <Label htmlFor="email" className="text-sm font-medium text-foreground">
                                            Work email
                                        </Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={form.email}
                                            onChange={onChange("email")}
                                            placeholder="you@company.com"
                                            className="h-11 bg-background"
                                            data-testid="contact-email-input"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <Label htmlFor="phone" className="text-sm font-medium text-foreground">
                                            Phone <span className="text-muted-foreground font-normal">(optional)</span>
                                        </Label>
                                        <Input
                                            id="phone"
                                            type="tel"
                                            value={form.phone}
                                            onChange={onChange("phone")}
                                            placeholder="+1 555 000 0000"
                                            className="h-11 bg-background"
                                            data-testid="contact-phone-input"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <Label htmlFor="message" className="text-sm font-medium text-foreground">
                                        How can we help?
                                    </Label>
                                    <Textarea
                                        id="message"
                                        value={form.message}
                                        onChange={onChange("message")}
                                        placeholder="Tell us about your products, target markets or sourcing needs…"
                                        rows={5}
                                        className="bg-background resize-none"
                                        data-testid="contact-message-input"
                                    />
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
                                    <p className="text-xs text-muted-foreground">
                                        By submitting, you agree to be contacted by Zaroor Global Trading.
                                    </p>
                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="h-11 px-6 bg-primary text-primary-foreground hover:bg-primary-variant"
                                        data-testid="contact-submit-button"
                                    >
                                        {loading ? "Sending…" : "Send message"}
                                        {!loading && <ArrowRight className="w-4 h-4" />}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>

                {/* Google Map embed - Casper, WY office */}
                <div className="mt-12 lg:mt-16">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
                                Headquarters
                            </p>
                            <h3 className="mt-1 text-xl lg:text-2xl font-semibold text-foreground tracking-tight">
                                Casper, Wyoming · USA
                            </h3>
                        </div>
                        <a
                            href="https://www.google.com/maps/dir/?api=1&destination=5830+E+2nd+St,+Casper,+WY+82609"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-variant transition-smooth self-start sm:self-auto"
                        >
                            <Navigation className="w-4 h-4" />
                            Get directions
                        </a>
                    </div>

                    <div className="relative rounded-2xl overflow-hidden border border-border shadow-soft bg-background">
                        <div className="aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] w-full">
                            <iframe
                                title="Zaroor Global Trading LLC — Casper, WY office"
                                src="https://www.google.com/maps?q=5830+E+2nd+St,+Casper,+WY+82609&hl=en&z=15&output=embed"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                loading="lazy"
                                allowFullScreen
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>

                        {/* Address overlay card */}
                        <div className="absolute left-4 bottom-4 sm:left-6 sm:bottom-6 max-w-[88%] sm:max-w-sm rounded-xl bg-background/95 backdrop-blur-md border border-border shadow-soft p-4 sm:p-5">
                            <div className="flex items-start gap-3">
                                <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary text-primary-foreground shrink-0">
                                    <MapPin className="w-4 h-4" />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
                                        Office address
                                    </p>
                                    <p className="mt-1 text-sm font-medium text-foreground leading-relaxed">
                                        5830 E 2nd St, STE 7000 #34793<br />
                                        Casper, WY 82609, USA
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
